import { jsx as _jsx } from "react/jsx-runtime";
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { supabase } from '@/lib/supabase/client';
const AuthContext = createContext(null);
export function AuthProvider({ children }) {
    const [state, setState] = useState({
        status: 'loading',
        session: null,
        user: null,
        isAnonymous: false,
    });
    /** Hydrate la session au démarrage et reste à l'écoute des changements. */
    useEffect(() => {
        let mounted = true;
        void supabase.auth.getSession().then(({ data }) => {
            if (!mounted)
                return;
            const user = data.session?.user ?? null;
            setState({
                status: 'ready',
                session: data.session,
                user,
                isAnonymous: !!user?.is_anonymous,
            });
        });
        const { data: sub } = supabase.auth.onAuthStateChange((_event, session) => {
            if (!mounted)
                return;
            const user = session?.user ?? null;
            setState({
                status: 'ready',
                session,
                user,
                isAnonymous: !!user?.is_anonymous,
            });
        });
        return () => {
            mounted = false;
            sub.subscription.unsubscribe();
        };
    }, []);
    const signInAnonymously = useCallback(async () => {
        const { error } = await supabase.auth.signInAnonymously();
        if (error) {
            throw error;
        }
    }, []);
    const signInWithMagicLink = useCallback(async (email) => {
        const { error } = await supabase.auth.signInWithOtp({
            email,
            options: {
                emailRedirectTo: window.location.origin,
                // shouldCreateUser: true par défaut → idempotent (signup ou login).
            },
        });
        if (error) {
            return { ok: false, error: error.message };
        }
        return { ok: true, needsConfirm: true };
    }, []);
    const signInWithPassword = useCallback(async (email, password) => {
        const { error } = await supabase.auth.signInWithPassword({ email, password });
        if (error) {
            return { ok: false, error: error.message };
        }
        return { ok: true };
    }, []);
    const signUpWithPassword = useCallback(async (email, password) => {
        const { data, error } = await supabase.auth.signUp({
            email,
            password,
            options: { emailRedirectTo: window.location.origin },
        });
        if (error) {
            return { ok: false, error: error.message };
        }
        // Si la confirmation email est ON dans Supabase, data.session est null
        // tant que l'user n'a pas cliqué le lien.
        return { ok: true, needsConfirm: !data.session };
    }, []);
    const resetPasswordForEmail = useCallback(async (email) => {
        const { error } = await supabase.auth.resetPasswordForEmail(email, {
            redirectTo: window.location.origin,
        });
        if (error) {
            return { ok: false, error: error.message };
        }
        return { ok: true, needsConfirm: true };
    }, []);
    const linkEmailToAnonymousAccount = useCallback(async (email) => {
        // Préserve l'auth.users.id courante : tous les profiles/scores/amis suivent.
        const { error } = await supabase.auth.updateUser({ email }, { emailRedirectTo: window.location.origin });
        if (error) {
            return { ok: false, error: error.message };
        }
        return { ok: true, needsConfirm: true };
    }, []);
    const signOut = useCallback(async () => {
        await supabase.auth.signOut();
    }, []);
    const value = useMemo(() => ({
        ...state,
        signInAnonymously,
        signInWithPassword,
        signUpWithPassword,
        resetPasswordForEmail,
        signInWithMagicLink,
        linkEmailToAnonymousAccount,
        signOut,
    }), [
        state,
        signInAnonymously,
        signInWithPassword,
        signUpWithPassword,
        resetPasswordForEmail,
        signInWithMagicLink,
        linkEmailToAnonymousAccount,
        signOut,
    ]);
    return _jsx(AuthContext.Provider, { value: value, children: children });
}
export function useAuth() {
    const ctx = useContext(AuthContext);
    if (!ctx) {
        throw new Error('useAuth must be used within <AuthProvider>');
    }
    return ctx;
}
